<template>
  <div>
    <Header></Header>
    <div class="col-12 main-content">
      <div class="row">
        <div class="col-10 ms-3 me-3 pt-3 padding-bottom-XXL">

          <!-- EVENT INFO -->
          <div v-if="currentEvent" class="row">
            <div class="col-12">
              <div class="channel-details-card p-3 mb-3">
                <h5 class="text-center mb-3">Selected Event</h5>

                <div class="row g-2">
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>Event ID:</strong> {{ currentEvent.eventId }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>First Matcher:</strong> {{ currentEvent.firstMatcher }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>Second Matcher:</strong> {{ currentEvent.secondMatcher }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>League:</strong> {{ currentEvent.league }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>League ID:</strong> {{ currentEvent.leagueId }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>Status:</strong> {{ currentEvent.status }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>Active:</strong> {{ currentEvent.active }}
                  </div>
                  <div class="col-12 col-md-3 user-info-item">
                    <strong>Order Number:</strong> {{ currentEvent.orderNumber }}
                  </div>
                </div>

              </div>
            </div>
          </div>
          <a role="button" class="mb-3 procricket-btn--reverse " @click="goBack"> ← Go Back </a>
          <hr class="solid">

          <!-- CHANNELS FOR THIS EVENT -->
          <h4 class="mt-4">Channels for this Event</h4>

          <div class="d-flex align-items-center mt-3">
            <button
                class="procricket-btn reorder-btn"
                :class="{ 'is-active': channelReorderMode }"
                @click="toggleChannelReorderMode"
            >
              <span class="reorder-btn__icon">{{ channelReorderMode ? '✓' : '≡' }}</span>
              {{ channelReorderMode ? 'Done Reorder' : 'Reorder Channels' }}
            </button>

            <span v-if="channelReorderMode" class="reorder-hint">
              Drag rows using the handle.
            </span>

          </div>
          <div class="table-responsive mt-3">
            <table class="table table-striped table-bordered">
              <thead>
              <tr>
                <th v-if="channelReorderMode" style="width: 34px;" class="text-center" > Drag to edit</th>
                <th>Channel Name</th>
                <th>Order Number</th>
                <th>Encoder</th>
                <th>Encoder Stream Name</th>
                <th>Input Sources from Encoder</th>
                <th>Output url Origin</th>
                <th>Output url Destination Channel</th>
                <th>Channel Status</th>
                <th>Channel Active</th>
                <th>Streams</th>
                <th>Refresh</th>
                <th>Edit</th>
                <th class="text-center">Remove</th>
              </tr>
              </thead>

              <!-- Draggable replaces tbody -->
              <draggable
                  tag="tbody"
                  v-model="eventChannelRows"
                  item-key="channelId"
                  :handle="'.drag-handle-row'"
                  :disabled="!channelReorderMode"
                  :animation="150"
                  ghost-class="drag-ghost"
                  chosen-class="drag-chosen"
                  @end="onChannelsDragEnd"
              >
                <template #item="{ element: row }">
                  <tr
                      :id="`channel-row-${row.channelId}`"
                      :class="[
                        channelRowClass(row),
                        channelReorderMode ? 'reorder-row drag-handle-row' : '',
                        selectedChannelRow?.channelId === row.channelId ? 'channel-selected' : ''
                      ]"
                  >
                    <td v-if="channelReorderMode" class="text-center">
                      <span class="drag-handle-icon" title="Drag to reorder">≡</span>
                    </td>
                    <td>
                      <span v-if="selectedChannelRow && row.channelName === selectedChannelRow.channelName" class="channel-name" >
                        {{ row.channelName }}
                      </span>
                      <span v-else> {{ row.channelName }} </span>
                      <div v-if="getChannelMismatchMessage(row)" class="channel-mismatch-text">
                        {{ getChannelMismatchMessage(row) }}
                      </div>
                    </td>

                    <td>{{ row.orderNumber }}</td>
                    <td>{{ row.encoder || '—' }}</td>
                    <td>{{ row.streamName || '—' }}</td>
                    <td>
                      <div v-if="row.inputSources.length">
                        <div v-for="(src, i) in row.inputSources" :key="i">{{ truncateText(src, 50) }}</div>
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>{{ row.origin || '—' }}</td>
                    <td>{{ row.destinationChannel || '—' }}</td>
                    <td>{{ row.rawChannel.status }}</td>

                    <td class="text-center">
                      <input type="checkbox"
                             :checked="row.rawChannel.active"
                             @change="toggleChannelActive(row.rawChannel)"
                             :disabled="channelReorderMode" />
                    </td>

                    <td class="centered">
                      <a role="button" class="procricket-btn two-line"
                         @click="channelReorderMode ? null : openChannelStreams(row)"
                         :class="{ disabled: channelReorderMode }"
                         :aria-disabled="channelReorderMode ? 'true' : 'false'" :tabindex="channelReorderMode ? -1 : 0">
                        Configure Encoder Streams
                      </a>
                    </td>
                    <td class="centered">
                      <button
                          class="btn-xs icon-btn" title="Reload stream metadata from encoder"
                          @click="reloadChannelMetadata({ channelId: row.channelId, encoder: row.encoder, stream_identifier: row.streamId })"
                          :disabled="channelReorderMode || !row.encoder || !row.streamId"
                      >
                        <img src="@/assets/reload.svg" alt="" class="btn-icon" />
                      </button>
                      <p>{{row.timestamp}}</p>
                    </td>
                    <td class="centered">
                      <router-link
                          :to="{ name: 'ChannelEdit', params: { channelId: row.rawChannel.channelId }, query: selectedApp ? { app: selectedApp } : {} }"
                          class="procricket-btn"
                          :class="{ disabled: channelReorderMode }"
                          :aria-disabled="channelReorderMode ? 'true' : 'false'"
                          @click.prevent="channelReorderMode"
                      >
                        Edit
                      </router-link>
                    </td>
                    <td class="centered">
                      <button type="button"
                        class="remove-btn"
                        title="Click to remove channel from this event"
                        @click.stop="channelReorderMode ? null : removeChannelFromEvent(row.rawChannel)"
                        :disabled="channelReorderMode"
                        aria-label="Remove channel from this event">
                        x
                      </button>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
          </div>

          <div class="d-flex justify-content-start mt-2">
            <button v-if="!selectedChannelRow" class="procricket-btn btn-sm secondary" @click="showAddChannelModal = true" :disabled="channelReorderMode">
              + Add Channel
            </button>
          </div>
          <!-- ADD CHANNEL MODAL -->
          <div v-if="showAddChannelModal" class="modal-overlay">
            <div class="modal-box box-outline">

              <div class="modal-header">
                <h5>Add Channel to Event</h5>
              </div>
              <div class="row align-items-center g-2">
                <div class="col">
                  <Multiselect
                      v-model="selectedUnassignedChannel"
                      :options="unassignedChannelsOptions"
                      track-by="id"
                      label="label"
                      valueProp="id"
                      placeholder="Select a channel..."
                      @change="assignChannelToEvent"
                  />
                </div>

                <div class="col-auto">
                  <button class="procricket-btn secondary" @click="closeAddChannelModal">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Load Xaccel stream selector only for xaccel providers -->
          <ChannelXaccelStreams
              v-if="selectedChannelRow"
              :key="selectedChannelRow.channelId"
              :row="selectedChannelRow"
              :hardcoded-data="hardcodedData"
              :init-stream-options="initStreamOptions"
              :init-loading="initLoading"
              :iptvStreams="iptvStreams"
              :iptvLoading="iptvLoading"
              :initialEncoderData="this.initialEncoderData"
              @iptv-load="loadIptvStreams"
              @init-load-stream-ids="handleInitLoadStreamIds"
              @init-select-stream="handleInitSelectStream"
              @confirm-selection="reloadChannelMetadata"
              @remove-channel-metadata="removeChannelMetadata"
              @clear-initial-encoder-data="initialEncoderData = null"
              @save-stream="handleSaveStream"
              @close="closeStreamEditor"
          />
        </div>
      </div>
    </div>
    <div class="mt-3">
      <div class="channel-details-card p-3">
        <div class="d-flex align-items-center justify-content-between">
          <h5 class="mb-0">How this page works</h5>

          <button
              class="btn btn-sm btn-outline-secondary"
              type="button"
              @click="showHelp = !showHelp"
          >
            {{ showHelp ? 'Hide' : 'Show' }}
          </button>
        </div>

        <!-- Help content goes BELOW the header row -->
        <EventBroadcastHelp :show="showHelp" />
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue'
import Header from '@/components/Header.vue'
import AdminService from "@/services/admin-service";
import Cookies from "js-cookie";
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import ChannelXaccelStreams from "@/components/ChannelXaccelStreams.vue";
import {buildOutputURL, parseMetadata, parseOutputUrl, validateChannelStreamName} from "@/utils/bapi-functions";
import {formatUnixTimestamp, normalizeList, truncateText} from "@/utils/general-functions"
import XaccelService from "@/services/xaccel-service";
import EventBroadcastHelp from "@/components/EventBroadcastHelp.vue";
import draggable from "vuedraggable";

export default defineComponent({
  name: 'EventBroadcast',
  components: {ChannelXaccelStreams, EventBroadcastHelp, Header, Multiselect, draggable },

  data() {
    return {
      selectedApp: '',
      ChannelsList: [],
      selectedUnassignedChannel: null,
      selectedChannelRow: null,
      showAddChannelModal: false,
      showHelp: true,
      channelReorderMode: false,
      channelReorderBeforeMap: null,
      eventChannelRows: [],
      hardcodedData: {
        encoders: [],
        origins: [],
        iptvLinks: [],
      },
      initStreamOptions: [],  // [{ id, name }]
      initialEncoderData: null,
      initLoading: false,
      iptvStreams: [],
      iptvLoading: false,
      editedEvent: {
        eventId: this.$route.params.eventId,
        firstMatcher: this.$route.params.firstMatcher,
        secondMatcher: this.$route.params.secondMatcher,
        league: this.$route.params.league,
        leagueId: this.$route.params.leagueId,
        orderNumber: this.$route.params.orderNumber,
        status: this.$route.params.status,
        active: this.$route.params.active,
        application: this.$route.params.application
      }
    };
  },

  computed: {
    currentEvent() {
      return this.editedEvent?.eventId ? this.editedEvent : null;
    },
    preselectedChannelId() {
      const v = this.$route.query.channelId;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    emptyEvent() {
      const raw = sessionStorage.getItem("events");
      const events = JSON.parse(raw || "[]");
      return events.find(e =>
          String(e.league).toLowerCase().includes("empty")
      );
    },
    unassignedChannelsOptions() {
      return this.ChannelsList.filter(c =>
          (c.eventIds || []).map(Number).includes(Number(this.emptyEvent.eventId)) && c.status === false
      ).map(c => ({
        id: c.channelId,
        label: `${c.channelName} (events: ${(c.eventIds || []).join(', ')} - ${this.emptyEvent.league})`,
        channel: c
      }));
    }
  },
  methods: {
    truncateText,
    channelRowClass(row) {
      const mismatch = !!this.getChannelMismatchMessage(row);
      return {
        'channel-editing': this.selectedChannelRow?.channelId === row.channelId,
        'channel-mismatch': mismatch
      };
    },
    closeStreamEditor() {
      this.initialEncoderData = null;
      this.selectedChannelRow = null
    },
    getChannelMismatchMessage(row) {
      if (!row?.rawChannel) return "Invalid row data.";
      const res = validateChannelStreamName(row.rawChannel);
      return res.isValid ? null : res.error;
    },
    async doLoadChannels() {
      this.ChannelsList = await AdminService.getChannelsList(this.selectedApp);
      this.buildEventChannelRows();
      this.applyPreselectChannel();
    },
    applyPreselectChannel() {
      if (!this.preselectedChannelId) return;
      if (!this.currentEvent?.eventId) return;
      const row = this.eventChannelRows.find(
          r => Number(r.channelId) === Number(this.preselectedChannelId)
      );
      if (row) {
        this.openChannelStreams(row);
        this.$nextTick(() => {
          const el = document.getElementById(`channel-row-${row.channelId}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    },
    closeAddChannelModal(){
      this.showAddChannelModal = false;
    },
    assignChannelToEvent(id) {
      if (!id) return;
      const option = this.unassignedChannelsOptions.find(o => o.id === id);
      if (!option) return;
      const channel = option.channel;
      const updatedChannel = {
        ...channel,
        eventId: this.currentEvent.eventId,
        active: null,
        status: null
      }
      AdminService.updateChannel(updatedChannel)
          .then(() => {
            this.doLoadChannels();
            this.closeAddChannelModal();
            setTimeout(() => {
              this.selectedUnassignedChannel = null;
            }, 50);
          });
    },
    async toggleChannelActive(channel) {
      try {
       const updatedChannel = {
          ...channel,
          status: null,
          active: !channel.active
        }
        await AdminService.updateChannel(updatedChannel);
        await this.doLoadChannels();
      } catch (err) {
        console.error("Failed to update this channel active:", err);
        alert("Could not update channel status.");
      }
    },
    async removeChannelFromEvent(channel) {
      try {
        if (!this.emptyEvent) {
          alert('Placeholder event not found.');
          return;
        }
        await AdminService.updateChannel({ ...channel, eventIds: [this.emptyEvent.eventId], active: null, status: null });
        this.closeStreamEditor();
        await this.doLoadChannels();
      } catch (err) {
        console.error("Failed to remove channel:", err);
      }
    },
    async loadIptvStreams({ providerName }) {
      this.iptvLoading = true
      this.iptvStreams = []
      try {
        const resp = await XaccelService.getIPTVData(providerName)

        const streams = resp?.data?.streams
        this.iptvStreams = Array.isArray(streams)
            ? streams
                .map(s => ({
                  name: String(s?.name ?? "").trim(),
                  url: String(s?.url ?? "").trim(),
                }))
                .filter(s => s.name && s.url)
            : []
      } catch (e) {
        this.iptvStreams = []
      } finally {
        this.iptvLoading = false
      }
    },
    openChannelStreams(row) {
      this.initialEncoderData = null;
      if (sessionStorage.getItem(row.channelId)) {
        this.selectedChannelRow = sessionStorage.getItem(row.channelId)
      } else {
        this.selectedChannelRow = row
      }
      this.showAddChannelModal = false
    },
    async handleSaveStream(payload) {
      try {
        const { encoder, stream_identifier, origin, streamApp, channelName, inputs } = payload
        const outputUrl = buildOutputURL(origin, streamApp, channelName, this.editedEvent.eventId)
        if (!outputUrl) {
          alert("Cannot save: the output URL needs the channel's origin and stream app.")
          return
        }

        // Match your YAML:
        // - output is a STRING (used as outputs[0].url)
        // - inputs is passed through to /source-update
        const configBody = {
          stream_identifier,
          output: outputUrl,
          inputs
        }
        const statusResp = await XaccelService.getStreamStatus(encoder, stream_identifier)
        if (statusResp === "running") {
          alert("Cannot save changes: stream is currently RUNNING. Please stop it first, then try again.")
          return
        }
        await XaccelService.updateXaccelConfig(encoder, configBody)

        await this.reloadChannelMetadata({
          channelId: this.selectedChannelRow.channelId,
          encoder,
          stream_identifier
        })
        alert("Insert complete");
      } catch (err) {
        console.error('Failed to save stream config', err)
        alert('Failed to update stream configuration.')
      }
    },
    async loadHardcodedData() {
      const cached = sessionStorage.getItem("hardcodedData");
      const data = cached? JSON.parse(cached) : await XaccelService.getHardcodedData();
      this.hardcodedData = {
        encoders: normalizeList(data.encoders),
        origins: normalizeList(data.origins),
        iptvLinks: normalizeList(data.iptvLinks),
      };
    },
    async getMetadata (encoder, id){
      return await XaccelService.getXaccelStreamMetadata(encoder, id);
    },
    async reloadChannelMetadata({ channelId, encoder, stream_identifier }) {
      try {
        const channel = this.ChannelsList.find(c => Number(c.channelId) === Number(channelId))
        if (!channel) return console.warn("Channel not found", channelId)
        const response = await this.getMetadata(encoder, stream_identifier)
        const timestamp = formatUnixTimestamp(Date.now());
        const updatedChannel = {
          ...channel,
          active: null,
          status: null,
          providersMetadata: JSON.stringify({ ...response, timestamp }),
        };

        await AdminService.updateChannel(updatedChannel)
        await this.doLoadChannels()

        const freshRow = this.eventChannelRows.find(r => Number(r.channelId) === Number(channelId))
        this.selectedChannelRow = null
        this.$nextTick(() => {
          this.selectedChannelRow = freshRow || null
        })

      } catch (err) {
        console.error("Failed to reload channel encoder data", err)
        alert("Could not reload stream encoder data.")
      }
    },
    async removeChannelMetadata({channelId}) {
      try {
        const channel = this.ChannelsList.find(c => Number(c.channelId) === Number(channelId))
        if (!channel) return console.warn("Channel not found", channelId)
        const updatedChannel = {
          ...channel,
          active: null,
          status: null,
          providersMetadata: "{}",
        };

        await AdminService.updateChannel(updatedChannel)
        await this.doLoadChannels()
        const fresh = this.ChannelsList.find(c => Number(c.channelId) === Number(channelId));
        if (fresh) {
          this.selectedChannelRow = null;
          this.$nextTick(() => {
            this.selectedChannelRow = fresh;
          });
        }
      } catch (err) {
        console.error("Failed to reload channel encoder data", err)
        alert("Could not reload stream encoder data.")
      }
    },

    async handleInitSelectStream({ encoder, stream_identifier }) {
      const channelId = this.selectedChannelRow?.channelId
      if (!channelId) return console.warn("No selectedChannelRow when selecting stream")
      this.initialEncoderData = await this.getMetadata(encoder, stream_identifier)
    },

    async handleInitLoadStreamIds({ encoder }) {
      try {
        this.initLoading = true
        this.initStreamOptions = []
        this.initialEncoderData = null

        const res = await XaccelService.getXaccelStreamIds(encoder)
        this.initStreamOptions = (res?.streams || []).map(s => ({
          id: s.id,
          name: s.name
        }))
      } catch (e) {
        console.error("Failed to load stream ids:", e)
        alert("Failed to load stream list from provider.")
      } finally {
        this.initLoading = false
      }
    },
    // NEW: toggle reorder mode (CHANNELS)
    async toggleChannelReorderMode() {
      // ENTER: snapshot current positions (index) by channelId
      if (!this.channelReorderMode) {
        this.channelReorderBeforeMap = Object.fromEntries(
            this.eventChannelRows.map((r, idx) => [String(r.rawChannel.channelId), idx])
        );
        this.channelReorderMode = true;
        this.closeStreamEditor();
        this.closeAddChannelModal();
        return;
      }
      this.channelReorderMode = false;

      const before = this.channelReorderBeforeMap || {};
      this.eventChannelRows = this.eventChannelRows.map((r, idx) => {
        const newOrder = idx + 1;
        return {
          ...r,
          orderNumber: newOrder,
          rawChannel: {
            ...r.rawChannel,
            orderNumber: newOrder,
          },
        };
      });
      const updates = [];
      for (let i = 0; i < this.eventChannelRows.length; i++) {
        const r = this.eventChannelRows[i];
        const id = String(r.rawChannel.channelId);

        const oldIdx = before[id];
        const newIdx = i;

        if (oldIdx === undefined || oldIdx !== newIdx) {
          // Persist rawChannel, because that's what updateChannel expects
          updates.push({
            ...r.rawChannel,
            active: null,
            status: null,
          });
        }
      }
      this.channelReorderBeforeMap = null;
      if (!updates.length) return;

      try {
        for (const ch of updates) {
          await AdminService.updateChannel(ch);
        }
        await this.doLoadChannels();
      } catch (err) {
        console.error("Failed updating reordered channels:", err);
        alert(err?.message || "Failed to save new channel ordering.");
        await this.doLoadChannels();
      }
    },
    onChannelsDragEnd() {
      if (!this.channelReorderMode) return;
      this.eventChannelRows = this.eventChannelRows.map((r, idx) => {
        const newOrder = idx + 1;
        return {
          ...r,
          orderNumber: newOrder,
          rawChannel: { ...r.rawChannel, orderNumber: newOrder },
        };
      });
    },

    buildEventChannelRows() {
      const eventChannels = this.ChannelsList
          .filter(c => (c.eventIds || []).map(Number).includes(Number(this.currentEvent.eventId)))
          .sort((a, b) => a.orderNumber - b.orderNumber);

      this.eventChannelRows = eventChannels.map(channel => {
        const parsed = parseMetadata(channel.providersMetadata);
        const outputUrl = parsed?.output || null;
        const { origin, destinationChannel } = parseOutputUrl(outputUrl);

        return {
          channelId: channel.channelId,
          channelName: channel.channelName,
          orderNumber: channel.orderNumber,
          encoder: parsed?.encoder || null,
          streamId: parsed?.id ?? null,
          streamName: parsed?.name ?? null,
          inputSources: parsed?.inputs || [],
          timestamp: parsed?.timestamp ?? null,
          outputUrl,
          origin,
          destinationChannel,
          rawChannel: channel
        };
      });
    },
    goBack() {
      this.$router.back();
    }
  },

  mounted() {
    const appCookie = Cookies.get('application');
    if (appCookie) this.selectedApp = appCookie;
    this.doLoadChannels();
    this.loadHardcodedData();
  }
});
</script>

<style src="../../assets/style.css"></style>
